import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";

const ANCHOR_LINKS = [
  { label: "I tre strumenti", target: "come-funziona" },
  { label: "Property Advisor", target: "property-advisor" },
];

const ROUTE_LINKS = [
  { label: "Proprietari", to: "/proprietari" },
  { label: "Come funziona", to: "/percorso" },
  { label: "Professionisti", to: "/professionisti" },
];

const TRAILING_ANCHOR_LINKS = [{ label: "FAQ", target: "faq" }];

function getRefFromCookie() {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|; )vg_ref=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

function withRefLocal(path) {
  const ref = getRefFromCookie();
  return ref ? `${path}${path.includes("?") ? "&" : "?"}ref=${encodeURIComponent(ref)}` : path;
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section tracking (only when on home)
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection("");
      return undefined;
    }
    const ids = [...ANCHOR_LINKS, ...TRAILING_ANCHOR_LINKS].map((l) => l.target);
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // pick the entry with the highest intersection ratio currently intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        rootMargin: "-30% 0px -55% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);

  const goToAnchor = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/scopri#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const isRouteActive = (to) => location.pathname === to;

  return (
    <header
      id="top"
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-all duration-200 ${
        scrolled ? "shadow-[0_4px_24px_rgba(0,0,0,0.08)]" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Logo size="text-xl md:text-2xl" />

        <nav className="hidden md:flex items-center gap-6 lg:gap-7">
          {ANCHOR_LINKS.map((l) => {
            const isActive = location.pathname === "/" && activeSection === l.target;
            return (
              <button
                key={l.target}
                data-testid={`nav-link-${l.target}`}
                data-active={isActive ? "true" : "false"}
                onClick={() => goToAnchor(l.target)}
                className={`text-[15px] font-medium transition-colors duration-200 ${
                  isActive ? "text-[#B40101]" : "text-[#0A0A0A] hover:text-[#B40101]"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          {ROUTE_LINKS.map((l) => {
            const isActive = isRouteActive(l.to);
            return (
              <Link
                key={l.to}
                data-testid={`nav-route-${l.to.replace("/", "")}`}
                data-active={isActive ? "true" : "false"}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`text-[15px] font-medium transition-colors duration-200 ${
                  isActive ? "text-[#B40101]" : "text-[#0A0A0A] hover:text-[#B40101]"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
          {TRAILING_ANCHOR_LINKS.map((l) => {
            const isActive = location.pathname === "/" && activeSection === l.target;
            return (
              <button
                key={l.target}
                data-testid={`nav-link-${l.target}`}
                data-active={isActive ? "true" : "false"}
                onClick={() => goToAnchor(l.target)}
                className={`text-[15px] font-medium transition-colors duration-200 ${
                  isActive ? "text-[#B40101]" : "text-[#0A0A0A] hover:text-[#B40101]"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          <a
            data-testid="nav-cta-button"
            href={withRefLocal("/orizzonti")}
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(180,1,1,0.25)]"
          >
            Inizia gratis <ArrowRight className="w-4 h-4" />
          </a>
        </nav>

        <button
          data-testid="navbar-hamburger"
          className="md:hidden p-2 -mr-2 text-[#0A0A0A]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Apri menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 bg-white border-t border-[#E5E5E5] ${
          open ? "max-h-[500px]" : "max-h-0"
        }`}
        data-testid="navbar-mobile-menu"
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {ANCHOR_LINKS.map((l) => {
            const isActive = location.pathname === "/" && activeSection === l.target;
            return (
              <button
                key={l.target}
                data-testid={`mobile-nav-link-${l.target}`}
                data-active={isActive ? "true" : "false"}
                onClick={() => goToAnchor(l.target)}
                className={`py-3 text-left text-[16px] font-medium border-b border-[#F5F5F5] transition-colors duration-200 ${
                  isActive ? "text-[#B40101]" : "text-[#0A0A0A]"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          {ROUTE_LINKS.map((l) => {
            const isActive = isRouteActive(l.to);
            return (
              <Link
                key={l.to}
                data-testid={`mobile-nav-route-${l.to.replace("/", "")}`}
                data-active={isActive ? "true" : "false"}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`py-3 text-left text-[16px] font-medium border-b border-[#F5F5F5] transition-colors duration-200 ${
                  isActive ? "text-[#B40101]" : "text-[#0A0A0A]"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
          {TRAILING_ANCHOR_LINKS.map((l) => {
            const isActive = location.pathname === "/" && activeSection === l.target;
            return (
              <button
                key={l.target}
                data-testid={`mobile-nav-link-${l.target}`}
                data-active={isActive ? "true" : "false"}
                onClick={() => goToAnchor(l.target)}
                className={`py-3 text-left text-[16px] font-medium border-b border-[#F5F5F5] transition-colors duration-200 ${
                  isActive ? "text-[#B40101]" : "text-[#0A0A0A]"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          <a
            data-testid="mobile-nav-cta-button"
            href={withRefLocal("/orizzonti")}
            className="mt-3 inline-flex items-center justify-center gap-2 bg-[#B40101] text-white font-semibold px-5 py-3 rounded-xl transition-all duration-200"
            onClick={() => setOpen(false)}
          >
            Inizia gratis <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
}
